/**
 * Small maths helpers that mirror the original's, so ported formulas can be read
 * side by side with the C++.
 */

import { Vector3 } from 'three';

/** `bluntmath.cpp` — clamp. */
export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/**
 * `bluntmath.cpp:34` — clamp to [min,max] then rescale to 0..1.
 * Appears constantly in the ball and animation code as a soft ramp.
 */
export function normalizedClamp(value: number, min: number, max: number): number {
  return (clamp(value, min, max) - min) / (max - min);
}

/**
 * The threshold below which the original treats a vector as having no direction.
 *
 * `Vector3::Normalize(ifNull)` (vector3.cpp:170-181) tests EACH COMPONENT against this,
 * not the overall length. That is not the same test — (9e-7, 9e-7, 9e-7) is longer than
 * the threshold but every component is under it — and the difference is far too small to
 * see. It is reproduced because getting it wrong is free to avoid and impossible to debug.
 */
const NULL_EPSILON = 0.000001;

function isNullVector(v: Vector3): boolean {
  return (
    Math.abs(v.x) < NULL_EPSILON && Math.abs(v.y) < NULL_EPSILON && Math.abs(v.z) < NULL_EPSILON
  );
}

/**
 * `Vector3::GetLength` (vector3.hpp:262-269) — the length, with short vectors flushed to
 * exactly zero. The original does this so that repeated normalisation of a nearly-still
 * object cannot amplify floating-point dust into a direction.
 */
export function vecLength(v: Vector3): number {
  const length = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
  return length < NULL_EPSILON ? 0 : length;
}

/**
 * `Vector3::GetNormalized(fallback)` — normalise, but return the fallback for a vector too
 * short to have a direction, instead of producing NaN. The original passes 0 in the ball
 * code, meaning a zero vector. Reproducing this matters: a stationary ball hits it
 * every tick.
 */
export function normalizedOrZero(v: Vector3, out = new Vector3()): Vector3 {
  if (isNullVector(v)) return out.set(0, 0, 0);
  return out.copy(v).divideScalar(Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z));
}

/** `Vector3::GetNormalized(fallback)` with a caller-chosen fallback vector. */
export function normalizedOr(v: Vector3, fallback: Vector3, out = new Vector3()): Vector3 {
  if (isNullVector(v)) return out.copy(fallback);
  return out.copy(v).divideScalar(Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z));
}

/** `Vector3::GetNormalizedTo(length)` — direction of `v`, rescaled. */
export function normalizedTo(v: Vector3, length: number, out = new Vector3()): Vector3 {
  if (isNullVector(v)) return out.set(0, 0, 0);
  return out.copy(v).multiplyScalar(length / Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z));
}

/**
 * `Vector3::GetNormalizedMax(max)` — shorten to `max` if longer, otherwise leave ALONE.
 *
 * A cap, not a normalisation: a vector shorter than `max` comes back unchanged and is
 * therefore NOT a unit vector. Two call sites in the physics solver depend on that, and
 * reading it as "normalise, then scale" changes the result.
 */
export function normalizedMax(v: Vector3, max: number, out = new Vector3()): Vector3 {
  if (vecLength(v) > max) return normalizedOrZero(v, out).multiplyScalar(max);
  return out.copy(v);
}

/**
 * `bluntmath.hpp:46-49` — bend a 0..1 ramp into an S-curve, `bias` deciding how far.
 * At bias 0 it is the identity; at bias 1 it is a full sine ease in and out.
 */
export function curve(source: number, bias = 1): number {
  return (Math.sin((source - 0.5) * Math.PI) * 0.5 + 0.5) * bias + source * (1 - bias);
}

/** `bluntmath.cpp:61-63` — the sign, with zero counting as positive. Never returns 0. */
export function signSide(n: number): number {
  return n >= 0 ? 1 : -1;
}

/**
 * `bluntmath.cpp:94-100` — shift `value` by whole (max-min) steps until it is inside.
 *
 * The original is a pair of while loops, so an infinite or NaN input hangs it. This is the
 * same arithmetic in closed form, which differs only in that it terminates.
 */
export function modulateIntoRange(min: number, max: number, value: number): number {
  const step = max - min;
  if (!(step > 0) || !Number.isFinite(value)) return value;
  let result = value;
  if (result < min) result += Math.ceil((min - result) / step) * step;
  if (result > max) result -= Math.ceil((result - max) / step) * step;
  return result;
}

/** Shorthand for the wrap the original applies to nearly every angle. */
export function wrapAngle(radians: number): number {
  return modulateIntoRange(-Math.PI, Math.PI, radians);
}

/**
 * `Vector3::GetAngle2D(test)` (vector3.cpp:283-289) — the signed angle FROM `test` TO `v`,
 * in the XY plane, wrapped into [-pi, pi].
 *
 * Two traps, both load-bearing:
 *
 *   1. THE LEADING MINUS. The original negates the atan2, so the sign is the opposite of
 *      the usual right-handed convention. Combined with football-forward being (0,-1,0),
 *      a POSITIVE result means the player's LEFT.
 *   2. ARGUMENT ORDER. In the C++ this is a method — `a.GetAngle2D(b)` — and every call
 *      site reads that way round. Here that is `angle2D(a, b)`. Swapping them negates the
 *      answer, which compiles, runs, and steers players the wrong way.
 *
 * Both arguments are expected to be normalised; the original says so in a comment and does
 * not check (vector3.hpp:95).
 */
export function angle2D(a: Vector3, b: Vector3): number {
  return modulateIntoRange(
    -Math.PI,
    Math.PI,
    -Math.atan2(a.x * b.y - a.y * b.x, a.x * b.x + a.y * b.y),
  );
}

/** `Vector3::GetAngle2D()` (vector3.cpp:276-280) — bearing of `v` from +X, in [0, 2pi). */
export function absoluteAngle2D(v: Vector3): number {
  const angle = Math.atan2(v.y, v.x);
  return angle < 0 ? angle + 2 * Math.PI : angle;
}

/**
 * `Vector3::Rotate2D` (vector3.cpp:317-322) — rotate about the up axis, leaving Z alone.
 * Counter-clockwise seen from above.
 */
export function rotate2D(v: Vector3, radians: number, out = new Vector3()): Vector3 {
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const x = v.x * cos - v.y * sin;
  const y = v.y * cos + v.x * sin;
  return out.set(x, y, v.z);
}

/**
 * `FixAngle` (animcollection.hpp:50-55) — engine bearings measure from +X, football
 * bearings measure from forward, which is (0,-1,0). This is the quarter turn between them.
 */
export function fixAngle(radians: number): number {
  return modulateIntoRange(-Math.PI, Math.PI, radians + 0.5 * Math.PI);
}
